import React, { useMemo } from 'react';
import { StyleSheet, View, Pressable } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import { useTranslation } from '@/hooks/useTranslation';
import Text from '@/components/Typography/Text';

export const COLLECTION_MEMORY_MODES = ['account', 'collection', 'off'];

const createStyles = colors => StyleSheet.create({
  options: {
    gap: 8,
  },
  option: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
    padding: 12,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: colors.border_color_secondary,
    backgroundColor: colors.solid_color_tertiary,
  },
  optionSelected: {
    borderColor: colors.text_color_secondary,
    backgroundColor: colors.solid_color_secondary,
  },
  radio: {
    width: 20,
    height: 20,
    borderRadius: 10,
    borderWidth: 2,
    borderColor: colors.border_color_secondary,
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 1,
  },
  radioSelected: {
    borderColor: colors.text_color_secondary,
  },
  radioDot: {
    width: 10,
    height: 10,
    borderRadius: 5,
    backgroundColor: colors.text_color_primary,
  },
  optionCopy: {
    flex: 1,
    gap: 2,
  },
});

export default function CollectionMemoryModeSelector({ mode, onChange, disabled = false }) {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const styles = useMemo(() => createStyles(colors), [colors]);
  const options = [
    ['account', 'memoryModeAccount', 'memoryModeAccountDesc'],
    ['collection', 'memoryModeCollection', 'memoryModeCollectionDesc'],
    ['off', 'memoryModeOff', 'memoryModeOffDesc'],
  ];

  return (
    <View style={styles.options}>
      {options.map(([id, titleKey, descriptionKey]) => {
        const selected = mode === id;
        const title = t(`pages.collections.${titleKey}`);
        return (
          <Pressable
            key={id}
            style={[styles.option, selected && styles.optionSelected]}
            onPress={() => onChange(id)}
            disabled={disabled}
            accessibilityRole="radio"
            accessibilityState={{ checked: selected, disabled }}
            aria-checked={selected}
            accessibilityLabel={title}
          >
            <View style={[styles.radio, selected && styles.radioSelected]}>
              {selected && <View style={styles.radioDot} />}
            </View>
            <View style={styles.optionCopy}>
              <Text size="s" weight="medium" color="primary">{title}</Text>
              <Text size="xs" color="secondary">{t(`pages.collections.${descriptionKey}`)}</Text>
            </View>
          </Pressable>
        );
      })}
    </View>
  );
}
